import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

const middleIndex = Math.ceil(categories.length / 2);
const categoryCol1 = categories.slice(0, middleIndex);
const categoryCol2 = categories.slice(middleIndex);

const categoryColors = [
  'bg-rose-50 text-rose-600',
  'bg-amber-50 text-amber-600',
  'bg-emerald-50 text-emerald-600',
  'bg-orange-50 text-orange-600',
  'bg-teal-50 text-teal-600',
  'bg-fuchsia-50 text-fuchsia-600',
  'bg-lime-50 text-lime-700',
  'bg-cyan-50 text-cyan-600',
  'bg-violet-50 text-violet-600',
  'bg-yellow-50 text-yellow-700',
  'bg-pink-50 text-pink-600',
  'bg-sky-50 text-sky-600',
  'bg-indigo-50 text-indigo-500',
  'bg-stone-50 text-stone-600',
];

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryClick = (url: string) => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-sm transition-colors duration-300">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <a
            href="/"
            className="flex items-center gap-2 text-foreground-950 hover:opacity-80 transition-opacity whitespace-nowrap cursor-pointer"
          >
            <span className="font-heading text-xl font-bold tracking-tight text-primary-500">Kakobuy</span>
            <span className="font-heading text-xl font-semibold text-foreground-600">Finds</span>
          </a>

          <div className="hidden md:flex items-center gap-4 flex-1 justify-center px-8">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-foreground-200 bg-transparent text-foreground-700 text-sm font-medium hover:border-foreground-400 hover:bg-background-100 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <span>Browse Categories</span>
                <i className={isDropdownOpen ? 'ri-arrow-up-s-line text-base transition-transform duration-200' : 'ri-arrow-down-s-line text-base transition-transform duration-200'}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[640px] bg-background-50 rounded-2xl border border-background-200 shadow-lg overflow-hidden">
                  <div className="grid grid-cols-2 gap-1 p-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.slug}
                        onClick={() => handleCategoryClick(cat.url)}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-left hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap group"
                      >
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-accent-100 transition-colors">
                          <i className={`${cat.iconClass} text-foreground-600 group-hover:text-accent-600 text-lg`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground-800 group-hover:text-foreground-950">{cat.name}</p>
                          <p className="text-xs text-foreground-500">{cat.itemCount.toLocaleString()} items</p>
                        </div>
                        <i className="ri-external-link-line text-foreground-400 group-hover:text-foreground-600 text-sm"></i>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Kakobuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full border-2 border-primary-500 text-primary-500 text-sm font-semibold hover:bg-primary-500 hover:text-background-50 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <i className={isMobileMenuOpen ? 'ri-close-line text-2xl text-foreground-800' : 'ri-menu-line text-2xl text-foreground-800'}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 max-h-[80vh] overflow-y-auto shadow-lg">
          <div className="px-4 py-5 space-y-6">
            <div>
              <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-1">
                Research Categories
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat, idx) => {
                  const colorClass = categoryColors[idx % categoryColors.length];
                  return (
                    <button
                      key={cat.slug}
                      onClick={() => handleCategoryClick(cat.url)}
                      className="flex flex-col items-center gap-2 p-3 rounded-xl bg-background-100 hover:bg-background-200 active:scale-[0.98] transition-all duration-150 cursor-pointer text-left"
                    >
                      <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${colorClass} transition-colors`}>
                        <i className={`${cat.iconClass} text-base`}></i>
                      </div>
                      <div className="text-center">
                        <p className="text-xs font-semibold text-foreground-800 leading-tight">{cat.name}</p>
                        <p className="text-[10px] text-foreground-500 mt-0.5">{cat.itemCount.toLocaleString()} items</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-background-200 pt-5 space-y-3">
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-1">
                Quick Links
              </p>
              <div className="grid grid-cols-2 gap-2">
                <a
                  href="/kakobuy-spreadsheet-guide"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100 text-xs font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <i className="ri-file-text-line text-accent-500 text-sm"></i>
                  Complete Guide
                </a>
                <a
                  href="/cssbuy-spreadsheet"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100 text-xs font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <i className="ri-file-text-line text-accent-500 text-sm"></i>
                  CSSBuy Guide
                </a>
                <a
                  href="/superbuy-spreadsheet"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100 text-xs font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <i className="ri-file-text-line text-accent-500 text-sm"></i>
                  Superbuy Guide
                </a>
                <a
                  href="/qc-spreadsheet"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100 text-xs font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <i className="ri-file-text-line text-accent-500 text-sm"></i>
                  QC Safety Guide
                </a>
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-2 pb-2 border-t border-background-200">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-3.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold text-center hover:bg-primary-600 transition-all active:scale-[0.98] cursor-pointer whitespace-nowrap"
              >
                Explore Kakobuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-3.5 rounded-xl border-2 border-primary-500 text-primary-500 text-sm font-semibold text-center hover:bg-primary-500 hover:text-background-50 transition-all active:scale-[0.98] cursor-pointer whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}